// Logica pura do Rodizio de grupos: link publico, selecao (ate 10), ordem e
// mensagens dos erros estaveis das RPCs.
import { redirectBaseUrl } from "@/lib/short-links";

export const MAX_ROTATOR_GROUPS = 10;
/** Limite de membros de um grupo do WhatsApp (referencia para "grupo cheio"). */
export const WHATSAPP_GROUP_LIMIT = 1024;

export interface AdminGroupOption {
  readonly jid: string;
  readonly title: string | null;
  readonly size: number | null;
}

export function rotatorUrl(code: string): string {
  return `${redirectBaseUrl()}/g/${code}`;
}

/** Liga/desliga um grupo na selecao, respeitando o teto de 10 (mantem a ordem). */
export function toggleSelection(selected: readonly string[], jid: string): readonly string[] {
  if (selected.includes(jid)) return selected.filter((j) => j !== jid);
  if (selected.length >= MAX_ROTATOR_GROUPS) return selected;
  return [...selected, jid];
}

/** Seleciona todos os visiveis ate o teto, preservando quem ja estava. */
export function selectAll(selected: readonly string[], visible: readonly string[]): readonly string[] {
  const merged = [...selected];
  for (const jid of visible) {
    if (merged.length >= MAX_ROTATOR_GROUPS) break;
    if (!merged.includes(jid)) merged.push(jid);
  }
  return merged;
}

/** Move o item de `from` para `to` (nova lista; indices fora da faixa = sem mudanca). */
export function moveItem<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}

export function filterGroups(groups: readonly AdminGroupOption[], query: string): readonly AdminGroupOption[] {
  const q = query.trim().toLocaleLowerCase("pt-BR");
  if (!q) return groups;
  return groups.filter((g) => (g.title ?? g.jid).toLocaleLowerCase("pt-BR").includes(q));
}

/** Grupo lotado: o resolvedor pula (whatsapp_group_member_limit() no banco). */
export function isGroupFull(size: number | null): boolean {
  return size !== null && size >= WHATSAPP_GROUP_LIMIT;
}

export interface ReceivingInput {
  readonly enabled: boolean;
  readonly invite_status: string;
  readonly invite_url: string | null;
  readonly size: number | null;
}

/** Mesma regra de group_rotator_group_receiving no banco: so esses recebem cliques. */
export function isGroupReceiving(g: ReceivingInput): boolean {
  return g.enabled && g.invite_status === "ready" && g.invite_url !== null && !isGroupFull(g.size);
}

export function isNearlyFull(size: number | null): boolean {
  return size !== null && size >= WHATSAPP_GROUP_LIMIT - 24;
}

const ERROR_MESSAGES: ReadonlyArray<readonly [string, string]> = [
  ["plan_required", "O Rodízio de grupos é um recurso do plano PRO."],
  ["not_admin", "O número conectado não é administrador de um dos grupos escolhidos. Atualize a lista e tente de novo."],
  ["whatsapp_not_connected", "Conecte o WhatsApp em Conexões para listar seus grupos."],
  ["invalid_group_count", `Escolha de 1 a ${MAX_ROTATOR_GROUPS} grupos.`],
  ["duplicate_group", "Há grupos repetidos na lista."],
  ["invalid_name", "Dê um nome de 1 a 60 caracteres ao rodízio."],
  ["rotator_limit", "Você chegou ao limite de 20 rodízios."],
  ["rotator_not_found", "Este rodízio não existe mais."],
  ["group_not_found", "Este grupo não está mais no rodízio."],
];

export function rotatorErrorMessage(raw: string): string {
  return ERROR_MESSAGES.find(([code]) => raw.includes(code))?.[1] ?? "Não foi possível concluir agora. Tente de novo em instantes.";
}

const INVITE_ERRORS: Record<string, string> = {
  not_admin: "o número não é mais admin",
  whatsapp_not_connected: "WhatsApp desconectado",
};

export function inviteErrorLabel(code: string | null): string {
  if (!code) return "erro ao gerar convite";
  return INVITE_ERRORS[code] ?? "erro ao gerar convite";
}
